import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
const sections = [{
  id: 'introduction',
  title: '1. Introduction'
}, {
  id: 'information-we-collect',
  title: '2. Information We Collect'
}, {
  id: 'how-we-use',
  title: '3. How We Use Your Information'
}, {
  id: 'sharing',
  title: '4. Sharing of Information'
}, {
  id: 'cookies',
  title: '5. Cookies and Tracking'
}, {
  id: 'security',
  title: '6. Data Storage and Security'
}, {
  id: 'international',
  title: '7. International Data Transfers'
}, {
  id: 'rights',
  title: '8. Your Rights'
}, {
  id: 'third-party',
  title: '9. Third-Party Links'
}, {
  id: 'updates',
  title: '10. Updates to This Policy'
}, {
  id: 'contact',
  title: '11. Contact Us'
}];
export function PrivacyPolicy() {
  const [activeSection, setActiveSection] = useState(sections[0].id);
  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => {
      const sectionElements = sections.map(s => document.getElementById(s.id));
      const scrollPosition = window.scrollY + 200; // Offset for sticky header
      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const section = sectionElements[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 120; // Header offset
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  return <main className="relative z-10 pt-32 pb-24 min-h-screen" data-id="element-1362">
      <div className="max-w-7xl mx-auto px-6" data-id="element-1363">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="max-w-3xl mb-16" data-id="element-1364">
          <h1 className="text-4xl md:text-5xl font-display mb-6" data-id="element-1365">
            Privacy Policy
          </h1>
          <p className="text-xl text-gray-400 font-medium mb-4" data-id="element-1366">
            How Aijentik collects, uses, and protects your information
          </p>
          <p className="text-sm text-brand-orange" data-id="element-1367">Last Updated: April 2026</p>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-16 relative" data-id="element-1368">
          {/* Main Content */}
          <div className="flex-1 max-w-3xl" data-id="element-1369">
            <div className="prose prose-invert prose-gray max-w-none" data-id="element-1370">
              {/* Section 1 */}
              <motion.section id="introduction" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1371">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1372">
                  1. Introduction
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1373">
                  <p data-id="element-1374">
                    Aijentik ("we", "our", "us") respects your privacy and is
                    committed to protecting your personal information. This
                    Privacy Policy explains how we collect, use, disclose, and
                    safeguard your information when you visit our website
                    aijentik.com or engage with our services.
                  </p>
                  <p data-id="element-1375">
                    We comply with applicable privacy laws, including the
                    Privacy Act 1988 (Cth) and international data protection
                    standards where applicable.
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1376" />

              {/* Section 2 */}
              <motion.section id="information-we-collect" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1377">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1378">
                  2. Information We Collect
                </h2>
                <div className="text-gray-300 space-y-6 leading-relaxed" data-id="element-1379">
                  <p data-id="element-1380">We may collect the following types of information:</p>

                  <div data-id="element-1381">
                    <h3 className="text-lg font-semibold text-white mb-3" data-id="element-1382">
                      a. Personal Information
                    </h3>
                    <ul className="list-disc pl-5 space-y-2 marker:text-brand-orange" data-id="element-1383">
                      <li data-id="element-1384">Name</li>
                      <li data-id="element-1385">Email address</li>
                      <li data-id="element-1386">Phone number</li>
                      <li data-id="element-1387">Company name</li>
                      <li data-id="element-1388">
                        Any information submitted via forms (e.g. demo requests,
                        AI reports)
                      </li>
                    </ul>
                  </div>

                  <div data-id="element-1389">
                    <h3 className="text-lg font-semibold text-white mb-3" data-id="element-1390">
                      b. Usage Data
                    </h3>
                    <ul className="list-disc pl-5 space-y-2 marker:text-brand-orange" data-id="element-1391">
                      <li data-id="element-1392">IP address</li>
                      <li data-id="element-1393">Browser type</li>
                      <li data-id="element-1394">Device information</li>
                      <li data-id="element-1395">Pages visited and time spent</li>
                      <li data-id="element-1396">Referral source</li>
                    </ul>
                  </div>

                  <div data-id="element-1397">
                    <h3 className="text-lg font-semibold text-white mb-3" data-id="element-1398">
                      c. AI Interaction Data
                    </h3>
                    <ul className="list-disc pl-5 space-y-2 marker:text-brand-orange" data-id="element-1399">
                      <li data-id="element-1400">Inputs provided into AI tools or forms</li>
                      <li data-id="element-1401">Website URLs submitted for analysis</li>
                      <li data-id="element-1402">
                        Generated outputs (for service delivery and improvement)
                      </li>
                    </ul>
                  </div>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1403" />

              {/* Section 3 */}
              <motion.section id="how-we-use" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1404">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1405">
                  3. How We Use Your Information
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1406">
                  <p data-id="element-1407">We use your information to:</p>
                  <ul className="list-disc pl-5 space-y-2 marker:text-brand-orange" data-id="element-1408">
                    <li data-id="element-1409">Provide and improve our services</li>
                    <li data-id="element-1410">Generate AI-driven insights and reports</li>
                    <li data-id="element-1411">Respond to enquiries and demo requests</li>
                    <li data-id="element-1412">Personalise your experience</li>
                    <li data-id="element-1413">Analyse website performance and user behaviour</li>
                    <li data-id="element-1414">Comply with legal obligations</li>
                  </ul>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1415" />

              {/* Section 4 */}
              <motion.section id="sharing" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1416">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1417">
                  4. Sharing of Information
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1418">
                  <p className="font-medium text-white" data-id="element-1419">
                    We do not sell your personal information.
                  </p>
                  <p data-id="element-1420">We may share information with:</p>
                  <ul className="list-disc pl-5 space-y-2 marker:text-brand-orange" data-id="element-1421">
                    <li data-id="element-1422">
                      Trusted service providers (hosting, analytics, CRM
                      systems)
                    </li>
                    <li data-id="element-1423">Professional advisers (legal, financial)</li>
                    <li data-id="element-1424">Authorities where required by law</li>
                  </ul>
                  <p className="text-sm text-gray-400 mt-4 italic" data-id="element-1425">
                    All third parties are required to handle your data securely.
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1426" />

              {/* Section 5 */}
              <motion.section id="cookies" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1427">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1428">
                  5. Cookies and Tracking
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1429">
                  <p data-id="element-1430">We use cookies and similar technologies to:</p>
                  <ul className="list-disc pl-5 space-y-2 marker:text-brand-orange" data-id="element-1431">
                    <li data-id="element-1432">Improve website functionality</li>
                    <li data-id="element-1433">Analyse traffic and usage patterns</li>
                    <li data-id="element-1434">Support marketing and remarketing efforts</li>
                  </ul>
                  <p data-id="element-1435">You can control cookies through your browser settings.</p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1436" />

              {/* Section 6 */}
              <motion.section id="security" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1437">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1438">
                  6. Data Storage and Security
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1439">
                  <p data-id="element-1440">
                    We take reasonable steps to protect your information using:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 marker:text-brand-orange" data-id="element-1441">
                    <li data-id="element-1442">Secure hosting environments</li>
                    <li data-id="element-1443">Encryption where appropriate</li>
                    <li data-id="element-1444">Access controls and authentication</li>
                  </ul>
                  <p className="text-sm text-gray-400 mt-4 italic" data-id="element-1445">
                    However, no system is completely secure.
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1446" />

              {/* Section 7 */}
              <motion.section id="international" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1447">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1448">
                  7. International Data Transfers
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1449">
                  <p data-id="element-1450">
                    As a global service, your data may be stored or processed
                    outside Australia. We take reasonable steps to ensure
                    overseas recipients comply with applicable privacy
                    standards.
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1451" />

              {/* Section 8 */}
              <motion.section id="rights" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1452">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1453">
                  8. Your Rights
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1454">
                  <p data-id="element-1455">Depending on your location, you may have rights to:</p>
                  <ul className="list-disc pl-5 space-y-2 marker:text-brand-orange" data-id="element-1456">
                    <li data-id="element-1457">Access your personal data</li>
                    <li data-id="element-1458">Request correction of inaccurate data</li>
                    <li data-id="element-1459">Request deletion of your data</li>
                    <li data-id="element-1460">Withdraw consent for certain uses</li>
                  </ul>
                  <p className="mt-6" data-id="element-1461">
                    To exercise your rights, contact us at:{' '}
                    <a href="mailto:privacy@aijentik.com" className="text-brand-orange hover:text-brand-deep-orange transition-colors" data-id="element-1462">
                      privacy@aijentik.com
                    </a>
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1463" />

              {/* Section 9 */}
              <motion.section id="third-party" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1464">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1465">
                  9. Third-Party Links
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1466">
                  <p data-id="element-1467">
                    Our website may contain links to third-party websites. We
                    are not responsible for their privacy practices.
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1468" />

              {/* Section 10 */}
              <motion.section id="updates" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1469">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1470">
                  10. Updates to This Policy
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1471">
                  <p data-id="element-1472">
                    We may update this Privacy Policy from time to time. Changes
                    will be posted on this page with an updated revision date.
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1473" />

              {/* Section 11 */}
              <motion.section id="contact" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1474">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1475">
                  11. Contact Us
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1476">
                  <p data-id="element-1477">
                    If you have any questions about this Privacy Policy, please
                    contact:
                  </p>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-6 mt-6" data-id="element-1478">
                    <p className="mb-2" data-id="element-1479">
                      <span className="text-white font-medium" data-id="element-1480">Aijentik</span>
                    </p>
                    <p className="mb-2" data-id="element-1481">
                      <span className="text-gray-400" data-id="element-1482">Email:</span>{' '}
                      <a href="mailto:privacy@aijentik.com" className="text-brand-orange hover:text-brand-deep-orange transition-colors" data-id="element-1483">
                        privacy@aijentik.com
                      </a>
                    </p>
                    <p data-id="element-1484">
                      <span className="text-gray-400" data-id="element-1485">Website:</span>{' '}
                      <a href="https://aijentik.com" className="text-brand-orange hover:text-brand-deep-orange transition-colors" data-id="element-1486">
                        aijentik.com
                      </a>
                    </p>
                  </div>
                </div>
              </motion.section>
            </div>
          </div>

          {/* Sticky Sidebar */}
          <div className="hidden lg:block w-72 shrink-0" data-id="element-1487">
            <div className="sticky top-32" data-id="element-1488">
              <h4 className="text-xs font-bold tracking-widest text-gray-500 uppercase mb-6" data-id="element-1489">
                Contents
              </h4>
              <nav className="flex flex-col space-y-1 border-l border-white/10" data-id="element-1490">
                {sections.map(section => <button key={section.id} onClick={() => scrollToSection(section.id)} className={`text-left px-4 py-2 text-sm transition-all duration-200 border-l-[3px] -ml-[2px] ${activeSection === section.id ? 'border-brand-orange text-white font-medium bg-white/5' : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-white/20'}`} data-id="element-1491">
                    {section.title}
                  </button>)}
              </nav>

              <div className="mt-12 p-6 bg-brand-orange/10 border border-brand-orange/20 rounded-xl" data-id="element-1492">
                <p className="text-sm text-brand-orange/90 leading-relaxed" data-id="element-1493">
                  If you have questions about your data, we're here to help.
                </p>
                <a href="mailto:privacy@aijentik.com" className="inline-block mt-4 text-sm font-medium text-white hover:text-brand-orange transition-colors" data-id="element-1494">
                  Contact Privacy Team →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>;
}